import React, { FC, useEffect, useContext } from 'react';
import { useToast } from '@chakra-ui/react';
import { Button } from '@design-system';
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';
import { WLContext } from '@contexts';
import { motion, AnimatePresence } from 'framer-motion';

import meta from '../meta.json';

// Flag a nivel módulo para evitar múltiples reloads simultáneos
// si varios assets con hash retornan 404 al mismo tiempo.
let isReloadingDueToStaleBuild = false;

export const ServiceWorkerWrapper: FC = () => {
  const toast = useToast();
  const { color } = useContext(WLContext);
  const brandColor = color || '#6344FF';

  const currentVersion = meta.semver ? `${meta.semver.raw}+${meta.distance}` : meta.raw;

  useEffect(() => {
    // Periodically check for updates
    const interval = setInterval(() => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.update();
        });
      }
    }, 60 * 1000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Escuchar mensajes del Service Worker
    // En particular, SW_ASSET_NOT_FOUND indica que el index.html cacheado
    // está pidiendo assets de una versión de build que ya no existe.
    // La solución es hacer un hard reload para obtener el nuevo index.html.
    const handleSwMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'SW_ASSET_NOT_FOUND') {
        if (!isReloadingDueToStaleBuild) {
          isReloadingDueToStaleBuild = true;
          console.warn(
            '[App] Service Worker reportó asset 404. ' +
            'El index.html está desactualizado. Recargando...',
          );
          // Hard reload: omite el caché del navegador y pide el recurso al servidor
          window.location.reload();
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSwMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSwMessage);
      }
    };
  }, []);


  useEffect(() => {
    serviceWorkerRegistration.register({
      onUpdate: async (registration) => {
        const waitingServiceWorker = registration.waiting;

        if (waitingServiceWorker) {
          let newVersion = '...';
          try {
            const response = await fetch('/meta.json', { cache: 'no-store' });
            const data = await response.json();
            newVersion = data.semver ? `${data.semver.raw}+${data.distance}` : data.raw;
          } catch (error) {
            console.error('Error fetching new version:', error);
          }

          waitingServiceWorker.addEventListener('statechange', (event: any) => {
            if (event.target.state === 'activated') {
              window.location.reload();
            }
          });

          if (!toast.isActive('sw-update-toast')) {
            toast({
              id: 'sw-update-toast',
              position: 'bottom-right',
              duration: null,
              isClosable: false,
              render: () => (
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                    className="mx-4 my-4"
                  >
                    {/* Modal Card - inspired by Modal component */}
                    <div className="relative flex flex-col rounded-lg border-0 bg-gray-15 shadow-lg outline-none focus:outline-none overflow-hidden"
                      style={{ maxWidth: '340px' }}
                    >
                      {/* Header */}
                      <div className="flex h-14 flex-row items-center justify-between rounded-t p-5 pb-0">
                        <h3 className="text-subtitle font-bold">
                          Actualización disponible
                        </h3>
                      </div>

                      {/* Body */}
                      <div className="relative flex-auto p-6 pt-3">
                        <p className="text-paragraph text-gray-600 mb-2">
                          Hay una nueva versión disponible con mejoras generales para tu aplicación.
                        </p>
                        <div className="flex items-center gap-2 text-paragraph-sm text-gray-400">
                          <span>{currentVersion}</span>
                          <span>→</span>
                          <span style={{ color: brandColor }} className="font-semibold">{newVersion}</span>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between p-6 pt-0">
                        <a
                          href="/changelog"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors uppercase tracking-wider"
                        >
                          Ver novedades
                        </a>
                        <div className="inline-block">
                          <Button
                            color={brandColor}
                            className="px-8"
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              waitingServiceWorker.postMessage({ type: 'SKIP_WAITING' });
                            }}
                          >
                            Refrescar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              ),
            });
          }
        }
      },
    });
  }, [toast, brandColor, currentVersion]);

  return null;
};
